import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Button } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useAuth } from '../../services/auth';
import { getUserMenu } from '../../services/menu';
import { Alert } from 'antd';
import passwordPolicyAPI from '../../services/passwordPolicy';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../BrandLogo';
import LanguageSwitcher from '../LanguageSwitcher';

const { Header, Sider, Content } = Layout;
const DEVICE_ACTIVITY_PATH = '/activities';
const LEGACY_DEVICE_ACTIVITY_PATH = '/logs';

const MainLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isPasswordChangeRequired } = useAuth();
  const { t } = useTranslation();
  
  // 检查是否需要强制修改密码
  const needsPasswordChange = isPasswordChangeRequired();

  // 加载密码策略
  const loadPasswordPolicy = async () => {
    try {
      const response = await passwordPolicyAPI.getPasswordPolicy();
      if (response.data && response.data.success) {
        setPasswordPolicy(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load password policy:', error);
    }
  };

  // 加载用户菜单
  useEffect(() => {
    console.log('=== MainLayout useEffect START ===');
    console.log('MainLayout useEffect - user:', user);
    console.log('MainLayout useEffect - user type:', typeof user);
    console.log('MainLayout useEffect - user role:', user?.role);
    console.log('MainLayout useEffect - localStorage user:', localStorage.getItem('user'));
    console.log('MainLayout useEffect - localStorage token:', localStorage.getItem('token'));
    
    let currentUser = user;
    
    // 如果没有用户但有 localStorage 中的用户数据，尝试恢复
    if (!currentUser && localStorage.getItem('user')) {
      try {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        console.log('MainLayout useEffect - Found stored user:', storedUser);
        if (storedUser && storedUser.role) {
          currentUser = storedUser;
        }
      } catch (error) {
        console.error('MainLayout useEffect - Error parsing stored user:', error);
      }
    }
    
    // 获取用户菜单
    const userMenu = getUserMenu(currentUser);
    console.log('MainLayout useEffect - userMenu:', userMenu);
    
    // 确保总是有菜单项
    if (userMenu && userMenu.length > 0) {
      setMenuItems(userMenu);
      console.log('MainLayout useEffect - menuItems set to:', userMenu);
    } else {
      console.log('MainLayout useEffect - No menu items found, setting fallback menu');
      // 设置回退菜单项（使用 i18n 文案）
      const fallbackMenuItems = [
        {
          key: '/dashboard',
          icon: React.createElement(UserOutlined),
          label: t('navbar.menu.dashboard'),
        },
        {
          key: '/devices',
          icon: React.createElement(UserOutlined),
          label: t('navbar.menu.device_management'),
        },
        {
          key: '/policies',
          icon: React.createElement(SettingOutlined),
          label: t('navbar.menu.policy_management'),
        },
        {
          key: DEVICE_ACTIVITY_PATH,
          icon: React.createElement(SettingOutlined),
          label: t('navbar.menu.device_activity_management'),
        },
        {
          key: '/settings',
          icon: React.createElement(SettingOutlined),
          label: t('navbar.menu.settings'),
        }
      ];
      setMenuItems(fallbackMenuItems);
      console.log('MainLayout useEffect - fallback menuItems set to:', fallbackMenuItems);
    }
    console.log('=== MainLayout useEffect END ===');
    
    // 如果需要强制修改密码，加载密码策略
    if (needsPasswordChange) {
      loadPasswordPolicy();
    }
  }, [user, needsPasswordChange, t]);

  // 添加一个单独的 useEffect 来监控 menuItems 的变化
  useEffect(() => {
    console.log('=== MenuItems changed ===');
    console.log('Current menuItems:', menuItems);
    console.log('MenuItems length:', menuItems.length);
    console.log('=== MenuItems changed END ===');
  }, [menuItems]);

  const handleMenuClick = ({ key }) => {
    // 如果需要强制修改密码，阻止导航
    if (needsPasswordChange && key !== '/profile') {
      return;
    }
    
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  const handleLanguageChange = () => {
    try {
      let currentUser = user;
      if (!currentUser && localStorage.getItem('user')) {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser && storedUser.role) {
          currentUser = storedUser;
        }
      }
      const updatedMenu = getUserMenu(currentUser);
      if (updatedMenu && updatedMenu.length > 0) {
        setMenuItems(updatedMenu);
      }
    } catch (e) {
      console.warn('刷新菜单以应用语言切换失败:', e);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: React.createElement(LockOutlined),
      label: t('layout.main.header.change_password'),
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: React.createElement(SettingOutlined),
      label: t('layout.main.header.settings'),
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: React.createElement(LogoutOutlined),
      label: t('layout.main.header.logout'),
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        width={240}
        collapsedWidth={80}
        style={{
          background: '#001529',
        }}
      >
        <div style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: '1px solid #303030'
        }}>
          <BrandLogo asset="dashboard" variant={collapsed ? 'mark' : 'full'} size={collapsed ? 'xs' : 'sm'} tone="dark" />
        </div>
        
        {/* 用容器保证 Sider 高度稳定 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname === LEGACY_DEVICE_ACTIVITY_PATH ? DEVICE_ACTIVITY_PATH : location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ borderRight: 0 }}
          />

          {/* 如果没有菜单项，显示提示 */}
          {menuItems.length === 0 && (
            <div style={{ color: 'white', padding: '20px', textAlign: 'center' }}>
              {t('layout.main.no_menu')}
            </div>
          )}
        </div>
      </Sider>
      
      <Layout>
        <Header 
          style={{ 
            padding: '0 24px', 
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0'
          }}
        >
          <Button
            type="text"
            icon={collapsed ? React.createElement(MenuUnfoldOutlined) : React.createElement(MenuFoldOutlined)}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          
          <Space>
            <span>{t('layout.main.header.welcome')}，{user?.display_name || user?.username || user?.email}</span>
            {/* Language switcher - top-right */}
            <LanguageSwitcher onLanguageChange={handleLanguageChange} />
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
            >
              <Avatar 
                icon={React.createElement(UserOutlined)} 
                src={user?.avatar}
                style={{ cursor: 'pointer' }}
              />
            </Dropdown>
          </Space>
        </Header>
        
        <Content
          style={{
            margin: '24px',
            padding: '24px',
            background: '#fff',
            minHeight: 280,
            borderRadius: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            overflow: 'auto',
            maxWidth: '100%'
          }}
        >
          {/* 强制密码修改提示 */}
          {needsPasswordChange && (
            <Alert
              message={t('layout.main.force_password.title')}
              description={
                <div>
                  <div>{t('layout.main.force_password.description')}</div>
                  {passwordPolicy && (
                    <div style={{ marginTop: 8 }}>
                      <strong>{t('layout.main.force_password.new_password_requirements')}：</strong>
                      {passwordPolicyAPI.formatPasswordRequirements(passwordPolicy).join('、')}
                    </div>
                  )}
                </div>
              }
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              action={
                <Button 
                  size="small" 
                  type="primary" 
                  danger
                  onClick={() => navigate('/profile?tab=password&force=true')}
                >
                  {t('layout.main.header.change_password')}
                </Button>
              }
            />
          )}
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
